import React, { useEffect } from 'react';

function parseCoordinateInput(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const numericValue = Number(String(value).replace(',', '.'));
  return Number.isFinite(numericValue) ? numericValue : null;
}

function formatCoordinate(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '—';
  }

  return numericValue.toFixed(6);
}

export function TreasureMapAddDrawer({
  isOpen = false,
  level = 0,
  canManageLocations = false,
  newLocation,
  setNewLocation,
  categories = [],
  onSubmit,
  onClose,
  onUseCurrentLocation,
  onToggleMapPick,
  isMapPickMode = false,
  userPos = null,
  isSubmitting = false,
}) {
  const canAddLocations = Number(level || 0) >= 5 || Boolean(canManageLocations);
  const safeLocation = newLocation || {
    title: '',
    desc: '',
    category: '',
    latitude: null,
    longitude: null,
  };
  const safeCategories = Array.isArray(categories) ? categories : [];
  const canUseCurrentLocation = Array.isArray(userPos) && userPos.length === 2;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !canAddLocations) {
    return null;
  }

  const updateField = (key, value) => {
    setNewLocation((previous) => ({
      ...(previous || {}),
      [key]: value,
    }));
  };

  return (
    <div className="treasure-map-add-drawer" aria-modal="false" role="dialog" aria-labelledby="treasure-map-add-drawer-title">
      <div className="treasure-map-add-drawer__handle" />

      <div className="treasure-map-add-drawer__header">
        <div>
          <div className="treasure-map-add-drawer__eyebrow">Neuer Kartenpunkt</div>
          <h3 id="treasure-map-add-drawer-title">Ort zur Schatzkarte hinzufügen</h3>
          <p>
            Freigeschaltet ab Level 5 oder mit WordPress-Rechten als Administrator, Redakteur oder Mitarbeiter.
          </p>
        </div>

        <button type="button" className="treasure-map-add-drawer__close" onClick={onClose}>
          Schließen
        </button>
      </div>

      <div className="treasure-map-add-drawer__tools">
        <button
          type="button"
          className="treasure-map-add-drawer__tool"
          onClick={onUseCurrentLocation}
          disabled={!canUseCurrentLocation}
        >
          📍 Aktuellen Standort übernehmen
        </button>

        <button
          type="button"
          className={`treasure-map-add-drawer__tool ${isMapPickMode ? 'is-active' : ''}`}
          onClick={onToggleMapPick}
        >
          {isMapPickMode ? '🗺️ Karten-Pick beenden' : '🗺️ Punkt auf Karte wählen'}
        </button>
      </div>

      {isMapPickMode && (
        <div className="treasure-map-add-drawer__hint is-info">
          Karten-Pick ist aktiv. Klicke jetzt auf die Karte, um die Koordinaten direkt zu übernehmen.
        </div>
      )}

      {!canUseCurrentLocation && (
        <div className="treasure-map-add-drawer__hint">
          Dein aktueller Standort ist gerade nicht verfügbar. Du kannst Koordinaten manuell eintragen oder per Karten-Pick setzen.
        </div>
      )}

      <form onSubmit={onSubmit} className="treasure-map-add-drawer__form">
        <div className="treasure-map-add-drawer__field-group">
          <label htmlFor="treasure-map-location-title">Name des Ortes</label>
          <input
            id="treasure-map-location-title"
            type="text"
            placeholder="z. B. Parkbank am Seiteneingang"
            value={safeLocation.title || ''}
            onChange={(event) => updateField('title', event.target.value)}
            required
          />
        </div>

        <div className="treasure-map-add-drawer__field-group">
          <label htmlFor="treasure-map-location-desc">Beschreibung</label>
          <textarea
            id="treasure-map-location-desc"
            placeholder="Optional: kurze Beschreibung, warum der Ort relevant ist"
            value={safeLocation.desc || ''}
            onChange={(event) => updateField('desc', event.target.value)}
            rows={3}
          />
        </div>

        <div className="treasure-map-add-drawer__field-group">
          <label htmlFor="treasure-map-location-category">Kategorie</label>
          <select
            id="treasure-map-location-category"
            value={safeLocation.category || ''}
            onChange={(event) => updateField('category', event.target.value)}
            required
          >
            <option value="" disabled>
              Kategorie auswählen
            </option>
            {safeCategories.map((category) => (
              <option key={category.key || category.name || category.label} value={category.name || category.label}>
                {category.label || category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="treasure-map-add-drawer__coordinates">
          <div className="treasure-map-add-drawer__field-group">
            <label htmlFor="treasure-map-location-lat">Breitengrad</label>
            <input
              id="treasure-map-location-lat"
              type="number"
              step="0.000001"
              inputMode="decimal"
              placeholder="51.050400"
              value={safeLocation.latitude ?? ''}
              onChange={(event) => updateField('latitude', parseCoordinateInput(event.target.value))}
              required
            />
          </div>

          <div className="treasure-map-add-drawer__field-group">
            <label htmlFor="treasure-map-location-lng">Längengrad</label>
            <input
              id="treasure-map-location-lng"
              type="number"
              step="0.000001"
              inputMode="decimal"
              placeholder="13.737300"
              value={safeLocation.longitude ?? ''}
              onChange={(event) => updateField('longitude', parseCoordinateInput(event.target.value))}
              required
            />
          </div>
        </div>

        <div className="treasure-map-add-drawer__coords-preview">
          Aktuelle Koordinaten: <strong>{formatCoordinate(safeLocation.latitude)}</strong> /{' '}
          <strong>{formatCoordinate(safeLocation.longitude)}</strong>
        </div>

        <div className="treasure-map-add-drawer__actions">
          <button type="button" className="treasure-map-add-drawer__secondary" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="treasure-map-add-drawer__primary" disabled={isSubmitting}>
            {isSubmitting ? 'Speichern …' : 'Ort hinzufügen'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TreasureMapAddDrawer;
